import {useMemo, useState} from 'react';
import {useField} from 'formik';
import {Select} from 'formik-antd';
import {useLocaleContext} from 'src/domain/utility/AppContextProvider/LocaleContextProvide';
import {IProductOption} from 'src/shared/services/product/dtos/product-option';
import usePagedSelectOptions, {
  PagedSelectParams,
  PagedSelectResponse,
} from 'src/shared/hooks/usePagedSelectOptions';

interface IPagedProductSelectProps {
  name: string;
  fetchPage: (
    params: PagedSelectParams,
  ) => Promise<PagedSelectResponse<IProductOption>>;
  mode?: 'multiple';
  // The edited record's saved products, so they show by name before (or
  // without) their page loading.
  savedItems?: Array<IProductOption>;
  onSelect?: (productId: string) => void;
  className?: string;
}

// Same page size and debounce as the push campaign product picker.
const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

const getProductName = (item: IProductOption, languageCode: string) =>
  item?.Locales?.find(
    (l) =>
      l?.LanguageId === languageCode &&
      // Stored as "Name" or "name" depending on where the product was saved.
      (!l?.LocaleKey || l.LocaleKey.toLowerCase() === 'name'),
  )?.LocaleValue || item?.Name;

// A product select searched on the server (by any language's name) that loads
// the next page as the list scrolls. The caller's fetchPage sets the scope;
// remount it (key) when that scope changes.
const PagedProductSelect = ({
  name,
  fetchPage,
  mode,
  savedItems,
  onSelect,
  className = 'width-100',
}: IPagedProductSelectProps) => {
  const {locale}: any = useLocaleContext();
  const [field] = useField(name);
  // Products picked here, kept offered once a search narrows or resets the list.
  const [picked, setPicked] = useState<Record<string, IProductOption>>({});

  const value = field.value;
  const pinnedItems = useMemo(() => {
    const selectedIds: Array<string> = Array.isArray(value)
      ? value
      : value
      ? [value]
      : [];
    return selectedIds
      .map(
        (id) => picked[id] ?? savedItems?.find((item) => item.Id === id),
      )
      .filter((item): item is IProductOption => !!item);
  }, [value, picked, savedItems]);

  const {items, loading, onSearch, onPopupScroll, onDropdownVisibleChange} =
    usePagedSelectOptions<IProductOption>(fetchPage, {
      pageSize: PAGE_SIZE,
      debounceMs: SEARCH_DEBOUNCE_MS,
      pinnedItems,
    });

  return (
    <Select
      className={className}
      name={name}
      mode={mode}
      allowClear
      showSearch
      loading={loading}
      filterOption={false}
      onSearch={onSearch}
      onPopupScroll={onPopupScroll}
      onDropdownVisibleChange={onDropdownVisibleChange}
      onSelect={(productId: any) => {
        const item = items.find((option) => option.Id === productId);
        if (item) setPicked((prev) => ({...prev, [item.Id]: item}));
        onSelect?.(productId);
      }}>
      {items.map((item) => (
        <Select.Option
          key={item.Id}
          value={item.Id}
          label={getProductName(item, locale.code)}>
          {getProductName(item, locale.code)}
        </Select.Option>
      ))}
    </Select>
  );
};

export default PagedProductSelect;
